/**
 * The socket contract, shared by the server and the client so a renamed event
 * is a compile error rather than a silent no-op at game time.
 */
/** How many samples to collect before trusting the offset. */
export const CLOCK_SAMPLE_COUNT = 10;
/** Re-run the handshake this often to correct for clock drift. */
export const CLOCK_RESYNC_INTERVAL_MS = 30_000;
/**
 * Estimates the offset between this client's clock and the server's.
 *
 * Takes the median of the lowest-latency half of the samples: a sample that
 * took a long time round-trip is more likely to have been delayed asymmetrically,
 * which is exactly the case where the midpoint assumption breaks down.
 */
export function estimateClockOffset(samples) {
    if (samples.length === 0) {
        return { offsetMs: 0, rttMs: 0, sampleCount: 0 };
    }
    const measured = samples.map((s) => ({
        offset: s.tServer - (s.t0 + s.t2) / 2,
        rtt: s.t2 - s.t0,
    }));
    measured.sort((a, b) => a.rtt - b.rtt);
    const keep = measured.slice(0, Math.max(1, Math.ceil(measured.length / 2)));
    const offsets = keep.map((m) => m.offset).sort((a, b) => a - b);
    const mid = Math.floor(offsets.length / 2);
    const offsetMs = offsets.length % 2 === 1
        ? offsets[mid]
        : (offsets[mid - 1] + offsets[mid]) / 2;
    const rttMs = keep.reduce((sum, m) => sum + m.rtt, 0) / keep.length;
    return { offsetMs, rttMs, sampleCount: samples.length };
}
/** The emoji anyone can send. Kept short: a wall of choices is worse than six. */
/**
 * The eight on the bar.
 *
 * Still eight, deliberately. The argument for a short list has not changed: a
 * picker you have to read is slower than saying the thing out loud, which
 * defeats the point of reacting without talking over the music. These are the
 * ones that get used.
 */
export const REACTION_EMOJI = ['🔥', '💀', '😭', '🐏', '👏', '🤨', '❤️', '🗑️'];
/**
 * The rest, behind a "more" button.
 *
 * The expanded roster people asked for, without putting a wall of choices in
 * front of the fast path. Nobody has to hunt through these to react, and
 * anybody who wants the specific one can go and get it.
 */
export const REACTION_EMOJI_EXTRA = [
    '😂', '🥶', '🤢', '🫡', '🙏', '👀', '🧠', '🤌',
    '📈', '📉', '🚨', '🎯', '🧊', '🪩', '🫠', '🗿',
];
/** Everything the server will accept. */
export const ALL_REACTION_EMOJI = [
    ...REACTION_EMOJI,
    ...REACTION_EMOJI_EXTRA,
];
//# sourceMappingURL=socket.js.map